<template>
  <section class="quadro" :aria-label="$t('clan.quadro.titulo')">
    <p class="quadro__rotulo">{{ $t('clan.quadro.titulo') }}</p>

    <ul v-if="itens.length" class="quadro__lista">
      <li v-for="evento in itens" :key="evento.id" class="quadro__linha">
        <FishIcon :nome="iconeDe(evento.tipo)" tamanho="miudo" />
        <span class="crescer">{{ frase(evento) }}</span>
        <span class="quadro__quando t-numero">{{ desdeQuando(evento.quando) }}</span>
      </li>
    </ul>

    <p v-else class="quadro__vazio">{{ $t('clan.quadro.vazio') }}</p>
  </section>
</template>

<script>
import { FishIcon } from '@/shared/ui';
import * as v2 from '@/core/services/api/v2';

/**
 * O QUADRO — o movimento do clã (D22, proposta 4).
 *
 * É o que faz o lugar parecer habitado, e resolve dois problemas do diagnóstico
 * de uma vez: "a contribuição some" (4) e "não há histórico" (2).
 *
 * **Não é o mural de avisos.** Este é escrito pelo SISTEMA e diz o que o clã
 * fez; aquele é escrito pelo líder.
 *
 * O nome de quem agiu vem GRAVADO na linha do servidor, e não de um JOIN — o
 * quadro tem que continuar legível depois que a pessoa saiu do clã. Aqui isso
 * aparece como `evento.quem` podendo ser nulo, e a frase se vira sem ele.
 */
const ICONES = {
  contributed: 'coins',
  boostUpgraded: 'arrow-up',
  boostActivated: 'bolt',
  memberJoined: 'user-plus',
  memberLeft: 'user-times',
  levelUp: 'star',
  biggestFish: 'fish',
  shopStocked: 'box',
  shopBought: 'shopping-cart',
  goalReached: 'bullseye',
  requestFilled: 'hand-holding-usd',
  flagRemoved: 'exclamation-triangle',
};

export default {
  name: 'ClanQuadro',
  components: { FishIcon },
  emits: ['erro'],
  data() {
    return { itens: [] };
  },
  created() {
    void this.carregar();
  },
  methods: {
    async carregar() {
      try {
        const r = await v2.quadroDoCla(this.$store.state.player.id, 20);
        this.itens = r.itens || [];
      } catch (error) {
        if (error?.response?.status !== 404) this.$emit('erro', error);
      }
    },

    iconeDe(tipo) {
      return ICONES[tipo] || 'info-circle';
    },

    /**
     * A frase de cada evento.
     *
     * As chaves do i18n recebem `quem`, `valor` e `alvo` como parâmetros, e cada
     * idioma monta a ordem que a língua dele pede. Concatenar aqui — "quem" + " "
     * + verbo — daria uma frase certa em português e errada em chinês.
     */
    frase(evento) {
      const quem = evento.quem || this.$t('clan.quadro.alguem');
      return this.$t(`clan.quadro.eventos.${evento.tipo}`, {
        quem,
        valor: evento.valor === null ? '' : new Intl.NumberFormat('pt-BR').format(evento.valor),
        alvo: evento.alvo || '',
      });
    },

    /** "há 2 h". Relativo porque o quadro é sobre o que é recente. */
    desdeQuando(iso) {
      const minutos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
      if (minutos < 60) return this.$t('clan.quadro.hMinutos', { n: minutos });
      const horas = Math.round(minutos / 60);
      if (horas < 24) return this.$t('clan.quadro.hHoras', { n: horas });
      return this.$t('clan.quadro.hDias', { n: Math.round(horas / 24) });
    },
  },
};
</script>

<style scoped>
.quadro {
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.quadro__rotulo {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

.quadro__lista {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* Uma linha por evento, sem cartão: o quadro é uma lista de recados a lápis, e
 * cartões dariam a vinte eventos o peso visual de vinte painéis. */
.quadro__linha {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  padding: var(--e-1) 0;
  border-bottom: 1px dashed var(--papel-sombra);
  font-size: var(--f-2);
  color: var(--tinta);
}

.quadro__quando {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  white-space: nowrap;
}

.quadro__vazio {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  font-style: italic;
}
</style>
